// hasUnsavedInput — does this page (or pane) hold work the user would lose if
// it were unmounted? (perf/shell-keepalive, review Delta 2, 2026-10-10)
//
//   el.call('hasUnsavedInput', pageOrPaneEl)   // or with `this` = that element
//
// A page is unsaved ONLY when it SAYS so. Two declarations count, anywhere in
// its element tree:
//   • `isDirty: (el, s) => boolean` — the page-level hook;
//   • `dirty` — the flag editors and AppDialog already set (a value or a fn).
// A declaration that throws counts as unsaved: a bug must never cost input.
//
// There is deliberately NO value heuristic. Comparing a field's live value with
// the value the framework rendered misread `{{ }}` template values and value
// functions that use their context, pinned pages with a page-local search box
// and pages whose form was already saved — and it never saw the field kinds
// below. A page with any of these MUST declare `isDirty` / `dirty` to be
// protected:
//   • select / AppSelect / AppSelectCompact;
//   • checkbox, radio, range, date, time, datetime-local, month, week, color,
//     file inputs;
//   • contenteditable (chat and mail Composer, tickets TicketDetailModal /
//     GeneralMain, every rich-text editor) and Monaco;
//   • widgets that write the value in code and fire only `change`
//     (date pickers, AppFieldChips, AppFieldSegmented, AppFieldColors);
//   • fields in a modal or dropdown rendered outside the page (ModalRoot).
// Typed text in a page that declares nothing is NOT protected.
//
// Used by the PageSlot keep-alive cap (canvas PageSlot `_hostPinned`) and the
// split-pane replace, with `confirmDiscardChanges` as the one prompt.
export function hasUnsavedInput(container) {
  const LIMIT = 20000
  const root = container || this
  if (!root || typeof root !== 'object') return false
  // A DISPOSED element holds no work: smbls `remove()` clears `__ref` but keeps
  // the element's own evaluated props (a removed page keeps `isDirty: true`).
  // A stale `root.__slotLiveHosts` entry must never make a split ask about a
  // page that is already gone (__tests__/hasUnsavedInputDisposedHost.test.js).
  if (!root.__ref) return false
  const declares = (el, prop) => {
    const v = el[prop]
    if (typeof v !== 'function') return !!v
    try {
      return !!v.call(el, el, el.state, el.context)
    } catch (err) {
      return true
    }
  }
  const stack = [root]
  let seen = 0
  while (stack.length && seen < LIMIT) {
    const el = stack.pop()
    seen++
    if (!el || typeof el !== 'object') continue
    if (declares(el, 'isDirty') || declares(el, 'dirty')) return true
    const kids = el.__ref && el.__ref.__children
    if (!Array.isArray(kids)) continue
    for (const k of kids) {
      const child = typeof k === 'string' ? el[k] : k
      if (child && typeof child === 'object' && child !== el) stack.push(child)
    }
  }
  return false
}
